import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["label", "checked", "onChange", "className", "canGetKeyboardFocus", "labelClickable", "defaultChecked", "noTopBottomSpacing", "isControlled"],
  _excluded2 = ["aria-label", "inputProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import Checkbox from '@mui/material/Checkbox';
import classnames from 'classnames';
import { EmbersCheckboxGroup } from './checkboxGroup';
import { createCheckboxTheme, NO_LABEL_CLASS, StyledEmbersLabeledBox } from './checkboxStyles';
import { LABEL_FOCUS_VISIBLE_CLASS } from '../labeledBox/labeledBoxStyles';
export var EmbersCheckbox = function EmbersCheckbox(_ref) {
  var label = _ref.label,
    checked = _ref.checked,
    onChange = _ref.onChange,
    className = _ref.className,
    _ref$canGetKeyboardFo = _ref.canGetKeyboardFocus,
    canGetKeyboardFocus = _ref$canGetKeyboardFo === void 0 ? true : _ref$canGetKeyboardFo,
    _ref$labelClickable = _ref.labelClickable,
    labelClickable = _ref$labelClickable === void 0 ? true : _ref$labelClickable,
    defaultChecked = _ref.defaultChecked,
    noTopBottomSpacing = _ref.noTopBottomSpacing,
    isControlled = _ref.isControlled,
    restProps = _objectWithoutProperties(_ref, _excluded);
  var ariaLabel = restProps['aria-label'],
    inputProps = restProps.inputProps,
    otherCheckboxProps = _objectWithoutProperties(restProps, _excluded2);
  var checkboxTheme = createCheckboxTheme();
  var _useState = useState(defaultChecked || false),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var checkboxRef = useRef(null);
  var collectCheckRef = function collectCheckRef(inputElem) {
    if (inputElem && inputElem.parentElement) {
      checkboxRef.current = inputElem.parentElement;
      inputElem.onfocus = delayedUpdateLabelClassList;
      inputElem.onblur = delayedUpdateLabelClassList;
    }
  };
  var setHoverStyle = function setHoverStyle() {
    var _checkboxRef$current;
    (_checkboxRef$current = checkboxRef.current) === null || _checkboxRef$current === void 0 || _checkboxRef$current.classList.add('EmbersCheckbox-hybridDeviceHover');
  };
  var removeHoverStyle = function removeHoverStyle() {
    var _checkboxRef$current2, _checkboxRef$current3;
    if ((_checkboxRef$current2 = checkboxRef.current) !== null && _checkboxRef$current2 !== void 0 && _checkboxRef$current2.classList.contains('EmbersCheckbox-hybridDeviceHover')) (_checkboxRef$current3 = checkboxRef.current) === null || _checkboxRef$current3 === void 0 || _checkboxRef$current3.classList.remove('EmbersCheckbox-hybridDeviceHover');
  };
  var onMouseEnter = function onMouseEnter() {
    // On PCs/laptops with touch screen, @media (hover: hover) and @media (any-hover: hover) cannot triggered,
    // and @media (any-pointer: coarse) and @media (pointer: coarse) cannot distinguish them with actual mobile devices,
    // so we need to handle mouse events on those PCs/laptops(on mobile devices there should not be mouse events)
    if (window.navigator.maxTouchPoints > 0) setHoverStyle();
  };
  var onMouseLeave = function onMouseLeave() {
    if (window.navigator.maxTouchPoints > 0) removeHoverStyle();
  };
  useEffect(function () {
    var _checkboxRef$current4, _checkboxRef$current5;
    (_checkboxRef$current4 = checkboxRef.current) === null || _checkboxRef$current4 === void 0 || _checkboxRef$current4.addEventListener('mouseenter', onMouseEnter);
    (_checkboxRef$current5 = checkboxRef.current) === null || _checkboxRef$current5 === void 0 || _checkboxRef$current5.addEventListener('mouseleave', onMouseLeave);
    return function () {
      var _checkboxRef$current6, _checkboxRef$current7;
      (_checkboxRef$current6 = checkboxRef.current) === null || _checkboxRef$current6 === void 0 || _checkboxRef$current6.removeEventListener('mouseenter', onMouseEnter);
      (_checkboxRef$current7 = checkboxRef.current) === null || _checkboxRef$current7 === void 0 || _checkboxRef$current7.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);
  useEffect(function () {
    typeof checked !== 'undefined' && checked !== value && setValue(checked);
  }, [checked]);
  var delayedUpdateLabelClassList = function delayedUpdateLabelClassList() {
    setTimeout(updateLabelClassList, 1);
  };

  // updateLabelClassList() moves the "focus-visible" class out to the label,
  //  to allow focus to be displayed around the label instead of just around the checkbox
  // NOTE that this is more complicated than usual (cf. useHoistFocusVisibleClass) because there is
  //  also the case where there is no label and we want to set a different class in a different place
  var updateLabelClassList = function updateLabelClassList() {
    if (canGetKeyboardFocus) {
      var checkboxElem = checkboxRef.current;
      if (checkboxElem) {
        var embersLabelBox;
        if (checkboxElem.parentElement.classList.contains('MuiFormControlLabel-root')) {
          embersLabelBox = checkboxElem.parentElement;
        }
        if (embersLabelBox) {
          checkboxElem.classList.remove(NO_LABEL_CLASS);
          if (checkboxElem.classList.contains('Mui-focusVisible')) {
            embersLabelBox.classList.add(LABEL_FOCUS_VISIBLE_CLASS);
          } else {
            embersLabelBox.classList.remove(LABEL_FOCUS_VISIBLE_CLASS);
          }
        } else {
          checkboxElem.classList.add(NO_LABEL_CLASS);
        }
      }
    }
  };
  useEffect(updateLabelClassList);
  var onDataChange = function onDataChange(event) {
    setValue(event.target.checked);
    onChange === null || onChange === void 0 || onChange(event, event.target.checked);
  };
  var checkboxItem = /*#__PURE__*/React.createElement(Checkbox, _extends({
    checked: value,
    onChange: onDataChange,
    tabIndex: !canGetKeyboardFocus ? -1 : undefined,
    disableRipple: true,
    inputRef: collectCheckRef,
    inputProps: _objectSpread({
      'aria-label': restProps['aria-label'],
      'aria-checked': checked
    }, inputProps),
    "aria-label": undefined
  }, otherCheckboxProps));
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: checkboxTheme
  }, /*#__PURE__*/React.createElement(StyledEmbersLabeledBox, {
    label: label || '',
    labelClickable: labelClickable,
    className: classnames({
      'EmbersCheckbox-wrap-noTopBottomSpacing': noTopBottomSpacing
    }, className),
    formControlProps: _objectSpread({
      control: checkboxItem,
      label: label || ''
    }, isControlled ? {
      onClick: function onClick(e) {
        e.preventDefault();
      }
    } : {}),
    disabled: restProps.disabled
  }, checkboxItem));
};
EmbersCheckbox.Group = EmbersCheckboxGroup;